import { UserAvatarRow } from 'components/UserAvatarRow';
import React, { useCallback, useMemo } from 'react';
import { FormatOptionLabelMeta, StylesConfig } from 'react-select';
import { getUserDisplayLabel } from 'utilities/fullName';
import {
  IUserSelectOption,
  resolveUserSelectOption
} from 'utilities/userSelectOption';
import { ISelect, Select } from '../Select/Select';

type UserSelectBaseProps = Omit<ISelect, 'options' | 'selectedOptions' | 'height'> & {
  options: IUserSelectOption[];
};

interface UserMultiSelect extends UserSelectBaseProps {
  isMulti?: true;
  selectedOptions?: IUserSelectOption[];
}

interface UserSingleSelect extends UserSelectBaseProps {
  isMulti?: false;
  selectedOptions?: IUserSelectOption;
}

const userSelectStyles: StylesConfig = {
  control: (provided) => ({
    ...provided,
    minHeight: '48px',
    height: 'auto'
  }),
  option: (provided) => ({
    ...provided,
    padding: '4px 8px'
  }),
  singleValue: (provided) => ({
    ...provided,
    position: 'relative',
    transform: 'none',
    top: undefined,
    maxWidth: 'calc(100% - 8px)'
  })
};

export const UserSelect: React.FC<UserMultiSelect | UserSingleSelect> = ({
  options,
  selectedOptions,
  isMulti,
  hideElements,
  ...props
}) => {
  const resolvedSelected = useMemo(
    () => resolveUserSelectOption(options, selectedOptions),
    [options, selectedOptions]
  );

  const formatOptionLabel = useCallback(
    (
      option: IUserSelectOption,
      meta: FormatOptionLabelMeta<IUserSelectOption>
    ) => {
      if (meta.context === 'value' && isMulti && !hideElements) {
        return getUserDisplayLabel({
          profile: option.profile,
          first_name: option.first_name,
          last_name: option.last_name,
          email: option.email,
          username: option.username
        });
      }

      return (
        <UserAvatarRow
          photo={option.photo ?? option.profile?.photo}
          profile={option.profile}
          first_name={option.first_name}
          last_name={option.last_name}
          email={option.email}
          username={option.username}
        />
      );
    },
    [hideElements, isMulti]
  );

  const getOptionLabel = (option: IUserSelectOption) =>
    getUserDisplayLabel({
      profile: option.profile,
      first_name: option.first_name,
      last_name: option.last_name,
      email: option.email,
      username: option.username
    });

  return (
    <Select
      {...props}
      isMulti={isMulti}
      hideElements={hideElements}
      options={options}
      selectedOptions={resolvedSelected}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      height={hideElements || isMulti ? undefined : 'auto'}
      styles={userSelectStyles}
    />
  );
};
